import { REACT_APP_COUNTRY_DEPLOY, REACT_APP_FLYWIRE_CODE_OTHER, REACT_APP_FLYWIRE_CODE_US } from "config";
import { getAmountWithTwoDecimalsAndMilles } from "services/common";

const DEFAULT_PAYMASTER = "1";
interface LinkResponse {
    id_subscription: number,
    id_quotation: string,
    id_application: string,
    policy_number: string,
    id_person: string,
    frequency: string,
    currency: string,
    country: string,
    language: string,
    first_name: string,
    middle_name: string,
    last_name: string,
    email: string,
    plan: string,
    term: string,
    payer: Payer,
    payments: PaymentPending[],
    has_paid_payments: boolean,
    gateway?: 'flywire' | 'stripe' | 'authnet',
}

export interface ErrorResponse {
    error: boolean;
    message: string;
    status: number;
}

interface Payer {
    first_name: string,
    last_name: string,
    middle_name: string,
    address1: string,
    address2: string,
    city: string,
    state: string,
    zip: string,
    country: string,
    phone: string,
    email: string,
    paymaster: string,
    paymaster_relationship: string,
}

interface PaymentPending {
    id_payment: number,
    id_transaction_provider: string,
    id_alternate_system: string,
    payment_date: string,
    status: string,
    amount: number,
    currency: string,
    expired: boolean,
}

interface Due {
    id: number;
    period: string;
    dueDate: string;
    paymentDate: string;
    currency: string;
    symbol: string;
    premium: string;
    amount: number;
    checked: boolean;
    withDelay: boolean;
    disabled: boolean;
}

interface TransformedData {
    idApplication: string | undefined;
    idQuotation: string | undefined;
    idPerson: string | undefined;
    insured: string | undefined;
    payer: string | undefined;
    policyOwner: string | undefined;
    policyNumber: string | undefined;
    plan: string | undefined;
    term: string | undefined;
    premium: string | undefined;
    paymentFrequency: string | undefined;
    firstName: string | undefined;
    payerFirstName: string | undefined;
    middleName: string | undefined;
    payerMiddleName: string | undefined;
    lastName: string | undefined;
    payerLastName: string | undefined;
    address: string | undefined;
    city: string | undefined;
    email: string | undefined;
    phone: string | undefined;
    amountStart: number | undefined;
    amount: number | undefined;
    country: string | undefined;
    dues: Due[];
    currency: string | undefined;
    symbol: string | undefined;
    recurrent?: boolean | undefined;
    language?: string | undefined;
    automaticApproval?: boolean | undefined;
    state: string | undefined;
    zipCode: string | undefined;
    payingForPolicy?: string | undefined;
    relationship?: string | undefined;
    paymentIds?: number[] | undefined;
    recipientCode?: string;
    hasPaidPayment?: boolean;
    gateway?: 'flywire' | 'stripe' | 'authnet';
}

function getDateShortLatin(date: string): string {
    if (!date || date === '' || date.length < 10) {
        return '';
    }
    const day = date.substring(8, 10);
    const month = date.substring(5, 7);
    const year = date.substring(0, 4);
    const formattedDate = `${day}/${month}/${year}`;

    return formattedDate
}

function getDateShortBrazil(date: string): string {
    if (!date || date === '' || date.length < 10) {
        return '';
    }
    const day = date.substring(8, 10);
    const month = date.substring(5, 7);
    const year = date.substring(0, 4);
    const formattedDate = `${year}-${month}-${day}`;

    return formattedDate
}

function getDateCustom(date: string, country: string): string {
    switch (country) {
        case 'BR':
            return getDateShortBrazil(date);
        case 'MX':
            return getDateShortLatin(date);
        default:
            return getDateShortLatin(date);
    }
}

function getTodayDateCustom(): string {
    const today = new Date();
    const country = REACT_APP_COUNTRY_DEPLOY;

    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0'); // Los meses comienzan desde 0
    const year = today.getFullYear();

    switch (country) {
        case 'BR':
            return `${year}-${month}-${day}`;
        case 'MX':
            return `${day}/${month}/${year}`;
        default:
            return `${month}/${day}/${year}`;
    }
}

const PAYMENT_WINDOW_COUNTRIES = ['BR'];

function hasPaymentWindow(country: string): boolean {
    return PAYMENT_WINDOW_COUNTRIES.includes(country);
}

function payableForCountry(payments: PaymentPending[] | undefined, country: string): PaymentPending[] {
    const pending = payments ?? [];
    return hasPaymentWindow(country) ? pending.slice(0, 1) : pending;
}

function transformLinkResponse(linkResponse: LinkResponse): TransformedData {
    let flywireCode = REACT_APP_FLYWIRE_CODE_OTHER;
    if (linkResponse.country === 'US') {
      flywireCode = REACT_APP_FLYWIRE_CODE_US;
    }
    const payments = payableForCountry(linkResponse.payments, REACT_APP_COUNTRY_DEPLOY);
    const firstPayment = payments[0];
    const transformedData: TransformedData = {
        idApplication: linkResponse?.id_application || '',
        idQuotation: linkResponse.id_quotation,
        idPerson: linkResponse.id_person,
        insured: `${linkResponse.first_name} ${linkResponse.last_name}`,
        payer: `${linkResponse.payer.first_name} ${linkResponse.payer.last_name}`,
        policyOwner: `${linkResponse.payer.first_name} ${linkResponse.payer.last_name}`,
        policyNumber: linkResponse?.policy_number || linkResponse?.id_application,
        plan: linkResponse?.plan,
        term: linkResponse?.term,
        premium: firstPayment ? `${linkResponse?.currency} $${getAmountWithTwoDecimalsAndMilles(firstPayment.amount, REACT_APP_COUNTRY_DEPLOY)}` : '',
        paymentFrequency: linkResponse.frequency,
        firstName: linkResponse.first_name,
        payerFirstName: linkResponse.payer.first_name,
        lastName: linkResponse.last_name,
        payerLastName: linkResponse.payer.last_name,
        middleName: linkResponse.middle_name,
        payerMiddleName: linkResponse.payer.middle_name,
        address: linkResponse.payer.address1,
        city: linkResponse.payer.city,
        email: linkResponse.payer.email,
        phone: linkResponse.payer.phone,
        amountStart: firstPayment?.amount ?? 0,
        amount: firstPayment && localStorage.getItem('selectedLinkOption') === 'payment' ? firstPayment.amount : 0.0,
        country: linkResponse.payer.country,
        dues: payments.map((payment, index) => ({
            id: payment.id_payment,
            period: '',
            dueDate: getDateCustom(payment.payment_date, REACT_APP_COUNTRY_DEPLOY),
            paymentDate: payment.payment_date,
            currency: payment.currency,
            symbol: '$',
            premium: getAmountWithTwoDecimalsAndMilles(payment.amount, REACT_APP_COUNTRY_DEPLOY),
            amount: payment.amount,
            checked: index === 0,
            withDelay: payment.expired,
            disabled: index > 1
        })),
        currency: linkResponse.currency,
        symbol: '$',
        recurrent: true,
        automaticApproval: true,
        zipCode: linkResponse.payer.zip,
        payingForPolicy: DEFAULT_PAYMASTER,
        relationship: linkResponse.payer.paymaster_relationship,
        state: linkResponse.payer.state,
        paymentIds: firstPayment ? [firstPayment.id_payment] : [],
        recipientCode: flywireCode,
        hasPaidPayment: linkResponse.has_paid_payments,
        gateway: linkResponse?.gateway,
    };
    return transformedData;
}

// Sums the real amount of every checked due (rounded to 2 decimals).
// Each due carries its own amount, so installments with different amounts
// add up correctly instead of assuming a single fixed installment value.
function sumCheckedDues(dues: Due[]): number {
    const total = dues.reduce((acc, due) => (due.checked ? acc + due.amount : acc), 0);
    return parseFloat(total.toFixed(2));
}

type LinkOption = 'payment' | 'update';

const UPDATE_OPTION: LinkOption = 'update';

function resolveLinkOption(search: string): LinkOption {
    return new URLSearchParams(search).get('option') === UPDATE_OPTION ? 'update' : 'payment';
}

function linkUrlWithOption(pathname: string, search: string, option: LinkOption): string {
    const params = new URLSearchParams(search);
    params.set('option', option);
    return `${pathname}?${params}`;
}

function canOpenLink(option: LinkOption, payments: PaymentPending[] | undefined): boolean {
    return option === UPDATE_OPTION || (payments?.length ?? 0) > 0;
}

// Amount shown on the payment confirmation. The checkout's confirmed amount
// arrives after the confirmation screen is already visible, so until then
// fall back to the installments already loaded from the payment link.
const resolveConfirmationAmount = (
    confirmedAmount: number,
    linkData?: { amount?: number; amountStart?: number } | null,
): number => {
    if (confirmedAmount > 0) return confirmedAmount;
    return linkData?.amount || linkData?.amountStart || 0;
};

export type { LinkResponse, TransformedData, LinkOption };
export { transformLinkResponse, getTodayDateCustom, sumCheckedDues, resolveLinkOption, canOpenLink, payableForCountry, hasPaymentWindow, linkUrlWithOption, resolveConfirmationAmount };
